/**
 * Safe localStorage wrapper that handles errors gracefully.
 * localStorage can throw in private browsing mode or when quota is exceeded.
 */

/**
 * Safely get an item from localStorage with JSON parsing.
 * @param key - The storage key
 * @param fallback - Default value if key doesn't exist or on error
 * @returns The parsed value or fallback
 */
export function getStorageItem<T>(key: string, fallback: T): T {
  try {
    if (typeof window === 'undefined') {
      return fallback;
    }
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Safely set an item in localStorage with JSON stringification.
 * @param key - The storage key
 * @param value - The value to store
 * @returns true if successful, false on error
 */
export function setStorageItem<T>(key: string, value: T): boolean {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely remove an item from localStorage.
 * @param key - The storage key
 * @returns true if successful, false on error
 */
export function removeStorageItem(key: string): boolean {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely get an item from sessionStorage with JSON parsing.
 * @param key - The storage key
 * @param fallback - Default value if key doesn't exist or on error
 * @returns The parsed value or fallback
 */
export function getSessionItem<T>(key: string, fallback: T): T {
  try {
    if (typeof window === 'undefined') {
      return fallback;
    }
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Safely set an item in sessionStorage with JSON stringification.
 * @param key - The storage key
 * @param value - The value to store
 * @returns true if successful, false on error
 */
export function setSessionItem<T>(key: string, value: T): boolean {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    sessionStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely remove an item from sessionStorage.
 * @param key - The storage key
 * @returns true if successful, false on error
 */
export function removeSessionItem(key: string): boolean {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}
